/* eslint-disable no-nested-ternary */
import { useCallback, useMemo } from "react";
import { OrderTypeResponse } from "../../../codegen-api";
import { AccountStateEnum } from "../../../contexts/AuthContext";
import { Button, ButtonThemeEnum } from "../../Buttons/styles";
import {
  playOrderCreatedAnimation,
  useMountedRef,
} from "../orderCreatedAnimation";
import { LiquidationsText } from "../style";
import { PerpFormFieldKeyEnum } from "./form";
import {
  computeBuyingPower,
  pickPerpFeeStructure,
  resolveOpeningFeeRate,
} from "./perpFees";
import { usePerpsTradeFormEffects } from "./usePerpsTradeFormEffects";

export function usePerpsTradeFormFees(
  ctx: ReturnType<typeof usePerpsTradeFormEffects>
) {
  const {
    accountData,
    perpInstrument,
    selectedLeverage,
    orderType,
    accountSigningKeyState,
    showOnboarding,
    setShowConnectModal,
    commonFormTranslations,
    account,
    currentPosition,
    deleteRFQsByInstrumentId,
    setClosePositionModal,
    animControls,
    resetField,
    setValue,
  } = ctx;

  const feeStructure = useMemo(
    () =>
      pickPerpFeeStructure(
        accountData?.fee_structures,
        perpInstrument?.underlying_asset
      ),
    [accountData?.fee_structures, perpInstrument?.underlying_asset]
  );

  // `undefined` when no verified fee schedule exists (null fees): see perpFees.
  const openingFeeRate = useMemo(
    () => resolveOpeningFeeRate(feeStructure),
    [feeStructure]
  );

  // 0 = unknown ("---"): an unvalued account OR an unknown fee rate.
  const buyingPower = useMemo(
    () =>
      computeBuyingPower(
        accountData,
        selectedLeverage,
        openingFeeRate,
        orderType === OrderTypeResponse.Market
      ),
    [accountData, openingFeeRate, orderType, selectedLeverage]
  );

  const replacePlaceOrderButton = useMemo(() => {
    if (accountSigningKeyState !== AccountStateEnum.OK || showOnboarding) {
      return (
        <Button
          fullWidth
          type="button"
          buttonTheme={ButtonThemeEnum.HIGHLIGHT}
          onClick={() => setShowConnectModal(true)}
        >
          {accountSigningKeyState === AccountStateEnum.REQUIRE_PASSWORD
            ? commonFormTranslations("unlock_trading")
            : accountSigningKeyState === AccountStateEnum.OK
            ? commonFormTranslations("continue_onboarding")
            : account
            ? commonFormTranslations("complete_sign_in")
            : commonFormTranslations("connect_wallet")}
        </Button>
      );
    }
    return undefined;
  }, [
    accountSigningKeyState,
    account,
    commonFormTranslations,
    setShowConnectModal,
    showOnboarding,
  ]);

  const replaceClosePositionButton = useMemo(() => {
    if (accountSigningKeyState === AccountStateEnum.OK && !showOnboarding) {
      if (accountData?.in_liquidation) {
        return (
          <LiquidationsText>
            {commonFormTranslations("liquidations_in_progress")}
          </LiquidationsText>
        );
      }
      return undefined;
    }
    return <div />;
  }, [
    accountSigningKeyState,
    showOnboarding,
    accountData?.in_liquidation,
    commonFormTranslations,
  ]);

  const onCloseModalHide = useCallback(() => {
    if (currentPosition)
      deleteRFQsByInstrumentId(currentPosition.instrument_id);
    setClosePositionModal(false);
  }, [currentPosition, deleteRFQsByInstrumentId]);

  const formMounted = useMountedRef();
  const showOrderCreatedAnimation = useCallback(
    () => playOrderCreatedAnimation(animControls, () => formMounted.current),
    [animControls, formMounted]
  );

  const resetAmountInput = useCallback(() => {
    resetField(PerpFormFieldKeyEnum.AMOUNT, {
      defaultValue: "0.0",
    });
    setValue(PerpFormFieldKeyEnum.AMOUNT, "0.0", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });
  }, [resetField, setValue]);

  return {
    ...ctx,
    feeStructure,
    openingFeeRate,
    buyingPower,
    replacePlaceOrderButton,
    replaceClosePositionButton,
    onCloseModalHide,
    showOrderCreatedAnimation,
    resetAmountInput,
  };
}
